import type { InitStripoOptions } from './stripo.types';
import defaultHtml from './defaultTemplate.html?raw';

// This supports an edge case where the script in index.html is not loaded yet, but the user clicks the button to open the editor.
const waitForEditor = (): Promise<void> => {
  return new Promise(resolve => {
    if (window.UIEditor) {
      resolve();
      return;
    }

    const POLL_INTERVAL = 100;

    const interval = setInterval(() => {
      if (window.UIEditor) {
        clearInterval(interval);
        resolve(void 0);
      }
    }, POLL_INTERVAL);
  });
};

// The editor is kept alive between opens to avoid re-initialization issues.
// We just reinitialize with new content using forceRecreate: true.
export const initStripo = async (options: InitStripoOptions): Promise<void> => {
  await waitForEditor();

  window.UIEditor!.initEditor(options.container, {
    forceRecreate: true,
    metadata: {
      emailId: options.emailId,
    },
    html: options.html || defaultHtml,
    css: options.css || '',
    locale: 'en',
    onTokenRefreshRequest: (callback: (token: string) => void) => {
      options
        .getToken()
        .then(callback)
        .catch(error => {
          console.error('Failed to refresh Stripo token:', error);
        });
    },
    messageSettingsEnabled: false,
    conditionsEnabled: false,
    syncModulesEnabled: false,
    notifications: {
      info: console.info,
      error: (message, id, params) => {
        // Filter out noisy errors
        if (message?.includes?.('connection')) {
          return;
        }
        console.error('Stripo error:', message, id, params);
      },
      warn: console.warn,
      success: console.log,
    },
    mergeTags: options.mergeFields,
    mobileViewButtonSelector: '.toggle-mobile-button',
    desktopViewButtonSelector: '.toggle-desktop-button',
    codeEditor: {
      isOpen: false,
      isDefaultCSSOpen: true,
      isCustomCSSOpen: false,
    },
    codeEditorButtonSelector: '.toggle-code-editor-button',
  });
};
